import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../api/ApiProvider.js';
import { useActiveClassroom } from '../../context/ActiveClassroomProvider.js';
import { useFitScale } from '../../hooks/useFitScale.js';
import { DrawingPad, type DrawingPadHandle } from '../../components/DrawingPad/index.js';
import { PlaceValueSection } from './PlaceValueSection.js';
import { ManipulativesSection } from './ManipulativesSection.js';
import { CurrencySection } from './CurrencySection.js';
import { NeighborsSection } from './NeighborsSection.js';
import { OddEvenToggle } from './OddEvenToggle.js';
import styles from './Number.module.css';

/**
 * Number page — drawing pads (place value, expanded form, neighbors),
 * manipulative blocks (hundred/ten/one), currency, and odd/even toggle.
 *
 * State is intentionally ephemeral. Strokes live in canvas memory;
 * blocks/coins/odd-even live in component state. Midnight clear (or a
 * page revisit) wipes everything via the registered DrawingPad refs.
 */
export function Number() {
  const api = useApi();
  const { active } = useActiveClassroom();

  // Per-classroom toggle: when off, render the pad boxes as plain
  // shells so teachers can annotate with the board's native pen
  // overlay. Defaults to enabled until the settings query lands so we
  // don't flash an empty layout for the common case.
  const settingsQuery = useQuery({
    queryKey: ['classroom-settings', active?.id ?? 'none'],
    queryFn: () => api.home.getSettings(active!.id),
    enabled: active !== null,
  });
  const drawingEnabled = settingsQuery.data?.numberDrawingEnabled ?? true;

  const allPadRefs = useRef<DrawingPadHandle[]>([]);
  const [resetCounter, setResetCounter] = useState(0);

  // The Number page is intrinsically tall (manipulatives + currency tray +
  // three drawing pads). The live stage clips overflow, so shrink the card to
  // fit the safe box when it can't fit at full size (never upscales).
  const pageRef = useRef<HTMLElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const fitScale = useFitScale(pageRef, cardRef);

  function registerPad(handle: DrawingPadHandle | null) {
    if (handle && !allPadRefs.current.includes(handle)) {
      allPadRefs.current.push(handle);
    }
  }

  function clearEverything() {
    for (const ref of allPadRefs.current) ref.clear();
    setResetCounter((c) => c + 1);
  }

  if (!active) {
    return (
      <section className={styles.empty} role="status">
        No classroom selected.
      </section>
    );
  }

  return (
    <section
      className={styles.numberPage}
      data-testid="number-page"
      key={resetCounter}
      ref={pageRef}
    >
      <div className={styles.card} ref={cardRef} style={{ transform: `scale(${fitScale})` }}>
        {/* Single 2-column layout — each column stacks vertically.
         *  Left:  Number modeling (top), Odd-or-Even (below — small,
         *         tucks neatly under the manipulatives).
         *  Right: Currency → Expanded form → Number neighbors (the
         *         wider section sits at the bottom of the right
         *         column where there's room for the 3 pads). */}
        <div className={styles.twoCol}>
          <div className={styles.col}>
            <ManipulativesSection />
            <OddEvenToggle />
          </div>
          <div className={styles.col}>
            <CurrencySection />
            <PlaceValueSection registerPad={registerPad} drawingEnabled={drawingEnabled} />
            <NeighborsSection registerPad={registerPad} drawingEnabled={drawingEnabled} />
          </div>
        </div>

        <footer className={styles.footer}>
          <button
            type="button"
            onClick={clearEverything}
            className={styles.clearButton}
            data-testid="num-clear-all"
          >
            Clear all
          </button>
        </footer>
      </div>
    </section>
  );
}

// Re-export DrawingPad for convenience.
export { DrawingPad };
